<template>
<!-- eslint-disable max-len -->
<div
  class="metrics"
  :style="metricsStyle"
  @mousewheel="scrollHorizontally"
  @mouseover="tempDisableAutoScroll"
  ref="scroller"
> <!-- static size scroll view -->
  <div
    class="metric-wheels"
    :style="metricWheelsStyle"
  > <!-- dynamic size flex box -->
    <radial-progress-nav
      v-for="({ metric, value }, i) in visibleCurrentMetrics"
      class="metric-wheel"
      :style="getMetricWheelStyle(i)"
      :key="metric._id"
      :value="value"
      :percent="!!metric.percentage"
      :integer="!!metric.integer"
      :min="metric.min"
      :max="metric.max"
      :color="colors[i % colors.length]"
      :name="metric.title"
      :labelBelow="labelsBelow"
      :pdf="pdf"
    > <!-- label and radial fill with display value -->
    </radial-progress-nav>
  </div>
</div>
</template>

<script>
import { mapGetters } from 'vuex';

import RadialProgressNav from './RadialProgressNav';

export default {
  components: {
    RadialProgressNav
  },
  props: {
    labelsBelow: { type: Boolean, default: false },
    noScroll: { type: Boolean, default: false },
    orderOverride: { type: Array },
    sizeOverride: { type: Number },
    sizesOverride: { type: Array },
    pdf: { type: Boolean, default: false }
  },
  mounted() {
    if (this.activeEnvironment != null && this.activeEnvironment.metricGraphBarColors != null) {
      const parsed = JSON.parse(this.activeEnvironment.metricGraphBarColors);
      if (parsed.length > 0) {
        this.colors = parsed;
      }
    }
    this.autoScroll();
  },
  data() {
    return {
      colors: ['dodgerblue', 'gold', 'mediumvioletred', 'forestgreen'],
      currentMetrics: [],
      autoScrollInterval: null,
      autoScrollDisableTimestamp: 0,
    };
  },
  computed: {
    ...mapGetters([
      'activeEnvironment',
      'displayMetrics',
      'displayTeamDecisionBeginMetrics',
      'currentState',
      'gameIntroState',
      'gameOutroState',
      'endState',
      'avatarSelectOutroState',
      'roleSelectOutroState',
      'dashboardState',
      'individualDecisionState',
      'resultsState',
      'teamDecisionState',
      'complete',
      'lastTeamDecision',
      'teamDecision'
    ]),
    showCash() {
      if (this.currentState === this.resultsState && !this.complete) {
        if (this.lastTeamDecision != null) {
          return !(this.lastTeamDecision.hideCashInDashboard);
        }
        return true;
      }
      if (this.teamDecision != null) {
        return !(this.teamDecision.hideCashInDashboard);
      }
      return true;
    },
    extraWide() {
      return !(this.showCash) && (this.visibleCurrentMetrics.length > 3);
    },
    metrics() {
      const oldMetricStates = [
        this.individualDecisionState,
        this.teamDecisionState,
        this.dashboardState
      ];
      const showOldMetrics = oldMetricStates.includes(this.$route.path);
      if (showOldMetrics) return this.displayTeamDecisionBeginMetrics;
      // display metrics accounts for metric splitting across challenges
      return this.displayMetrics;
    },
    visibleCurrentMetrics() {
      return this.currentMetrics.map((m, index) =>
        ((this.orderOverride && this.orderOverride.length >= this.currentMetrics.length)
          ? this.currentMetrics[this.orderOverride[index]]
          : m)
      ).filter(({ metric, hidden }) => {
        if (!hidden && metric && !metric.hidden) {
          if (this.currentState !== this.gameIntroState
            && this.currentState !== this.gameOutroState
            && this.currentState !== this.endState) {
            // decision hidden metric list should only take effect after
            // the game intro and before the game outro
            if (this.currentState === this.resultsState && !this.complete
              && this.lastTeamDecision != null && this.lastTeamDecision.hiddenTeamMetrics != null) {
              return this.lastTeamDecision.hiddenTeamMetrics.every(hiddenMetric => (
                (hiddenMetric.showInDashboard != null && hiddenMetric.showInDashboard)
                  || hiddenMetric._id !== metric._id
              ));
            }
            if (this.teamDecision != null && this.teamDecision.hiddenTeamMetrics != null) {
              return this.teamDecision.hiddenTeamMetrics.every(hiddenMetric => (
                (hiddenMetric.showInDashboard != null && hiddenMetric.showInDashboard)
                  || hiddenMetric._id !== metric._id
              ));
            }
          }
          return true;
        }
        return false;
      });
    },
    metricWheelsBaseCount() {
      return (this.extraWide) ? 5 : 3; // count of metrics before it would scroll
    },
    metricWheelsOverflowCount() {
      return this.visibleCurrentMetrics.length - this.metricWheelsBaseCount;
    },
    metricWheelsBaseWidth() {
      return (this.extraWide) ? 2943.4 : 1810; // non-scrolling width of container
    },
    metricWheelsOverflowWidth() {
      return (574 * Math.max(0, this.metricWheelsOverflowCount));
    },
    metricWheelsWidth() {
      return this.metricWheelsBaseWidth + this.metricWheelsOverflowWidth;
    },
    metricWheelsStyle() {
      return {
        // make room for scroll bar
        transform: (this.sizeOverride)
          ? `scale(${this.sizeOverride})`
          : (this.isScrollable)
            ? 'scale(0.31)'
            : ((this.extraWide && !(this.labelsBelow))
              ? 'scale(0.32) translateY(10px)'
              : undefined),
        // set scroll bounds (has to be done manually due to transform)
        width: `${this.metricWheelsWidth}px`,
        'flex-wrap': ((this.noScroll) ? 'wrap' : 'nowrap'),
      };
    },
    metricsStyle() {
      return {
        width: ((this.extraWide) ? '1000px' : '730px'),
        overflow: ((this.noScroll) ? 'visible' : ((this.isScrollable) ? undefined : 'hidden')),
        'background-color': (this.noScroll && this.labelsBelow) ? 'transparent' : 'white',
      };
    },
    isScrollable() {
      return !(this.noScroll)
        && (this.metricWheelsOverflowCount > 0)
        && (this.metricWheelsOverflowWidth > 0);
    }
  },
  methods: {
    scrollHorizontally(e) {
      e.preventDefault();
      e.currentTarget.scrollLeft += e.deltaY;
      this.tempDisableAutoScroll();
    },
    tempDisableAutoScroll() {
      this.autoScrollDisableTimestamp = Date.now();
    },
    autoScroll() {
      if (!(this.isScrollable)) return;
      const steps = 1000;
      const period = 27000;
      const step = Math.max(1, this.metricWheelsOverflowWidth / steps); // px
      this.autoScrollInterval = setInterval(() => {
        if (!(this.isScrollable)) return clearInterval(this.autoScrollInterval);
        if (this.autoScrollDisableTimestamp) {
          if (Date.now() - this.autoScrollDisableTimestamp > 2000) { // 2 seconds
            this.autoScrollDisableTimestamp = 0;
          } else {
            return;
          }
        }
        const scroller = this.$refs.scroller;
        if (scroller) {
          if (scroller.offsetWidth + scroller.scrollLeft >= scroller.scrollWidth - 1) {
            scroller.scrollLeft = 0;
          } else {
            if (scroller.scrollLeft === 0) {
              this.tempDisableAutoScroll(); // wait 2 seconds
            }
            scroller.scrollLeft += step;
            if (scroller.offsetWidth + scroller.scrollLeft >= scroller.scrollWidth - 1) {
              this.tempDisableAutoScroll(); // wait 2 seconds before reset
            }
          }
        }
      }, period / steps);
    },
    getMetricWheelStyle(index) {
      if (this.sizesOverride == null
        || this.sizesOverride.length === 0
        || this.sizesOverride.length <= index
        || !(this.sizesOverride[index])) {
        return {};
      }
      return {
        transform: `scale(${this.sizesOverride[index]})`
      };
    },
  },
  created() {
    this.currentMetrics =
      this.metrics.map(({ metric, value, hidden }) => ({ metric, value, hidden }));
  },
  beforeDestroy() {
    if (this.autoScrollInterval) {
      clearInterval(this.autoScrollInterval);
    }
  }
};
</script>

<style lang="scss" scoped>
.metrics {
  overflow-y: hidden;
  overflow-x: auto;
  position: absolute;
  background-color: white;
  width: 730px;
  height: 90px;
  top: 0;
  left: 0;
  padding-left: 40px;
}
.metric-wheels {
  background-color: transparent;
  display: flex;
  flex-flow: row;
  transform: scale(0.37);
  transform-origin: top left;
  align-items: center;
  flex-wrap: wrap;
  width: 1810px;
}
.metric-wheel {
  margin-right: 120px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/game/ui/MetricsNavbar.vue